import * as React from 'react';
import { Badge, Button, Text, Icon } from '@/hen-ui/atomics';
import { Card, Flex, StatStyles } from '@/hen-ui/composites';
import { Callout, type CalloutProps } from '@/hen-ui/composites/Callout';
import type {
  BarItem,
  StatStylesValueColor,
} from '@/hen-ui/composites/StatStyles';
import { SparklineChart } from '@/hen-ui/composites/SparklineChart';
import {
  Tooltip,
  type TooltipContentFormat,
  type TooltipTriggerMode,
} from '@/hen-ui/composites/Tooltip';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';
import {
  defaultFormatDeltaValue,
  deltaToSemantic,
  getDeltaBadgeClassName,
  getDeltaBadgeVariant,
  getDeltaPresentation,
  parseDisplayValue,
  valueToSemantic,
} from './kpi-banner.utils';
import {
  displayValueNumber,
  resolveDisplayValue,
  type DisplayValue,
} from '../number-format';

export type KPIDirectionality = 'higher-is-better' | 'lower-is-better';

/** Stat layout for the tile body (default: headline number). */
export type KPIDisplayMode =
  | 'big-number'
  | 'bars-vertical'
  | 'bars-horizontal'
  | 'sparkline';

export interface KPITileProps {
  label: string;
  /** Optional help shown via the info-icon Tooltip beside the label. */
  labelTooltip?: string;
  /** Preferred side for {@link labelTooltip} (defaults to `top`). */
  labelTooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  /** Content format for {@link labelTooltip} (defaults to `plain`). */
  labelTooltipFormat?: TooltipContentFormat;
  /** How {@link labelTooltip} opens (defaults to `hover`; only one mode is active). */
  labelTooltipTrigger?: TooltipTriggerMode;
  /**
   * Primary metric for big-number layout (or summary when using bars), as
   * display text or as a number with the format to render it from.
   *
   * Describing it is preferred when the caller has the number: `colorValue:
   * 'auto-by-value'` needs the sign, and a described number gives it directly
   * rather than by parsing the text back.
   */
  value: DisplayValue;
  /**
   * Raw delta for directionality and arrow sign; null or zero renders neutral.
   */
  delta: number | null;
  directionality: KPIDirectionality;
  /** Optional display for the delta; defaults to a simple numeric string when absent. */
  deltaLabel?: DisplayValue;
  /** Optional secondary label rendered beside the delta badge (e.g. "vs last year"). */
  deltaSubLabel?: string;
  /** Defaults to big-number. Bars modes require non-empty bars. */
  statStyle?: KPIDisplayMode;
  /** Segment data for vertical or horizontal bar layouts. */
  bars?: BarItem[];
  /** Optional scale cap for bar proportions (see StatStyles max). */
  barsMax?: number;
  /**
   * Controls big-number value colouring.
   *
   * - `true` — dynamic: color follows `delta` + `directionality`, the same
   *   logic as the badge — positive outcome → green, negative → red, neutral
   *   (null / zero delta) → no color.
   * - `'positive'` — always green regardless of `delta` or directionality.
   * - `'negative'` — always red regardless of `delta` or directionality.
   * - `'auto-by-value'` — dynamic by the displayed value's own sign +
   *   `directionality`. Use when the tile has no comparator/delta but the
   *   value itself carries meaning (signed metric, variance, NPS).
   *   Uses the same `'auto-by-value'` vocabulary as chart `SeriesColorConfig`
   *   (see WaterfallChart), but is evaluated against the tile’s displayed value.
   *   Zero / unparseable values render neutral.
   * - `false` / omitted — no colouring (default).
   *
   * Does not affect how `delta` is displayed.
   * @default false
   */
  colorValue?: boolean | 'positive' | 'negative' | 'auto-by-value';
  /**
   * Time-series data for sparkline mode. Each point has an x label and numeric y value.
   * Required when `statStyle === 'sparkline'`; falls back to big-number when absent or empty.
   */
  trendData?: { x: number | string; y: number }[];
  /** Line style for the sparkline. Defaults to 'solid'. */
  sparklineLineStyle?: 'solid' | 'dashed';
}

/** Presentation outcome for a tile delta row. */
export type KPIDeltaSemantic = 'positive' | 'negative' | 'neutral';

export interface KPIDeltaPresentation {
  semantic: KPIDeltaSemantic;
  /** Dynamic icon name; null when neutral. */
  arrowIcon: 'arrow-up' | 'arrow-down' | null;
  /** Text + icon color classes (HEN tokens). */
  toneClassName: string;
}

export type { BarItem };

/**
 * CSS custom property name for theming KPI tile minimum width (`min-w-kpi-tile` in Tailwind).
 * Default fallback is `140px`
 */
export const KPI_TILE_MIN_WIDTH = '--min-width-kpi-tile';

/**
 * CSS custom property name for theming the max width of a `callout` banner item
 * (`max-w-kpi-callout` in Tailwind). Default fallback is `360px`. Callout items
 * do not grow with metric tiles; this caps how much of the row they occupy.
 */
export const KPI_CALLOUT_MAX_WIDTH = '--max-width-kpi-callout';

/** Metric tile item — the default banner block (headline number, bars, or sparkline). */
export type KPIBannerMetricItem = KPITileProps & { type?: 'metric' };

/**
 * Callout banner block (AI insight / recommendation). Same `Callout` composite
 * used standalone; defaults to the `neutral-primary` surface so it reads as a
 * distinct-but-cohesive block beside white metric tiles. Any `CalloutStyle`
 * remains selectable.
 */
export type KPIBannerCalloutItem = CalloutProps & { type: 'callout' };

/** Ordered, mixed banner block: a metric tile or a callout. */
export type KPIBannerItem = KPIBannerMetricItem | KPIBannerCalloutItem;

type KPIBannerDivProps = Omit<React.HTMLAttributes<HTMLElement>, 'children'>;

/**
 * Requires either `tiles` or `items` (or both). When both are set, a non-empty
 * `items` array wins; an empty `items` array falls back to `tiles` so hosts that
 * briefly hold `items={[]}` while assembling a mixed row do not blank the banner.
 */
export type KPIBannerProps = KPIBannerDivProps & {
  /** Accessible name for the metrics region. */
  'aria-label'?: string;
} & (
    | {
        tiles: KPITileProps[];
        items?: KPIBannerItem[];
      }
    | {
        items: KPIBannerItem[];
        tiles?: KPITileProps[];
      }
  );

function isCalloutItem(item: KPIBannerItem): item is KPIBannerCalloutItem {
  return item.type === 'callout';
}

/** Strip the banner discriminant and return a typed Callout props object. */
function toCalloutProps({
  type: _type,
  ...props
}: KPIBannerCalloutItem): CalloutProps {
  return props;
}

function resolveBannerItems(
  tiles: KPITileProps[] | undefined,
  items: KPIBannerItem[] | undefined,
): KPIBannerItem[] {
  if (items != null && items.length > 0) {
    return items;
  }
  return (tiles ?? []).map((tile) => ({ type: 'metric' as const, ...tile }));
}

function isBarsStatLayout(
  statStyle: KPIDisplayMode | undefined,
  bars: KPITileProps['bars'],
): boolean {
  return (
    (statStyle === 'bars-vertical' || statStyle === 'bars-horizontal') &&
    !!bars &&
    bars.length > 0
  );
}

/**
 * Returns true when `statStyle === 'sparkline'` but trendData is missing,
 * empty, or contains no numeric y values — triggering the big-number fallback.
 */
function isSparklineFallback(
  statStyle: KPIDisplayMode | undefined,
  trendData: KPITileProps['trendData'],
): boolean {
  if (statStyle !== 'sparkline') return false;
  const valid =
    trendData?.filter((d) => typeof d.y === 'number' && !Number.isNaN(d.y)) ??
    [];
  return valid.length === 0;
}

/**
 * Horizontal row of KPI metric tiles with optional delta and directionality-based coloring.
 *
 * **ChartFrame:** Pass as `children` with `surface="ready"` so loading, query error, and
 * empty states stay in the container; KPI children are **not mounted** until `surface` is
 * `ready`. Use `minHeightClassName` on the container (e.g. `min-h-0`) when this row is the only
 * content so the shell does not reserve a tall chart minimum height.
 *
 * `min-w-0` on the root helps horizontal scrolling behave inside flex parents (including
 * `ChartFrame`).
 *
 * **Layout (4–6 tiles typical):** Tiles use **`grow shrink-0 basis-auto`** with **`min-w-kpi-tile`**
 * (theme token in `tailwind.config.ts`, default 140px so each
 * tile is at least that wide (and wider when content needs it), extra space is shared with
 * `flex-grow`, and the row does not squeeze tiles below their flex base—**`overflow-x-auto`**
 * on the section scrolls when the sum of tile widths exceeds the viewport. Inner nodes use
 * **`min-w-0`** for truncation.
 *
 * **UI-only:** This composite does not fetch data or encode product rules. The host builds
 * **`tiles`** / **`items`** from API or query results (mapping layer lives outside hen-ui)
 * or passes hand-authored props. Prefer `items` for mixed metric + callout rows. A non-empty
 * `items` array wins over `tiles`; an empty `items` array falls back to `tiles`.
 */
export const KPIBanner = React.forwardRef<HTMLElement, KPIBannerProps>(
  (
    {
      tiles,
      items,
      className,
      'aria-label': ariaLabel = 'Key metrics',
      ...rest
    },
    ref,
  ) => {
    const resolvedItems = resolveBannerItems(tiles, items);

    return (
      <section
        ref={ref}
        aria-label={ariaLabel}
        data-hen-id={COMPONENT_IDS.KPIBanner}
        className={cn('w-full min-w-0 overflow-x-auto', className)}
        {...rest}
      >
        <div className='flex min-w-0 w-full flex-nowrap items-stretch gap-sm pb-0.5'>
          {resolvedItems.map((item, index) => {
            if (isCalloutItem(item)) {
              const calloutProps = toCalloutProps(item);
              return (
                <div
                  key={`callout-${index}`}
                  data-kpi-callout
                  className='flex w-full max-w-kpi-callout shrink-0 grow-0 basis-auto'
                >
                  <Callout
                    {...calloutProps}
                    style={calloutProps.style ?? 'neutral-primary'}
                    className={cn('h-full w-full', calloutProps.className)}
                  />
                </div>
              );
            }
            const { type: _type, ...tile } = item;
            return <KPITile key={`${tile.label}-${index}`} {...tile} />;
          })}
        </div>
      </section>
    );
  },
);
KPIBanner.displayName = 'KPIBanner';

/** Big-number value: block + truncate for equal flex columns. */
const kpiTileBigNumberStatClassName =
  'min-w-0 gap-xxs [&>span]:block [&>span]:min-w-0 [&>span]:truncate';

/**
 * Resolves the semantic `valueColor` token for `StatStyles` from the
 * KPI tile's delta + directionality + colorValue setting.
 *
 * `colorValue`:
 * - `true`             — delta-based: derives sentiment from `getDeltaPresentation(delta, directionality)`.
 *                        Neutral delta (null / 0) → `'default'` (no color).
 * - `'positive'`       — always `'success'` (caller explicitly marks the value as good news).
 * - `'negative'`       — always `'error'` (caller explicitly marks the value as bad news).
 * - `'auto-by-value'`  — value-based: derives sentiment from
 *                        `valueToSemantic(rawValue, directionality)`. Zero or
 *                        unparseable `rawValue` → `'default'`.
 */
function getValueColor(
  delta: number | null,
  directionality: KPIDirectionality,
  colorValue: 'positive' | 'negative' | 'auto-by-value' | true,
  rawValue: number | null,
): StatStylesValueColor {
  // Forced modes: ignore delta + value entirely.
  if (colorValue === 'positive') return 'success';
  if (colorValue === 'negative') return 'error';

  // Value-based: ignore delta, derive sentiment from the displayed
  // value's own sign + directionality.
  if (colorValue === 'auto-by-value') {
    const semantic = valueToSemantic(rawValue, directionality);
    if (semantic === 'positive') return 'success';
    if (semantic === 'negative') return 'error';
    return 'default';
  }

  // Dynamic (colorValue === true): derive sentiment from delta + directionality,
  // matching the badge arrow logic so both always agree.
  const { semantic } = getDeltaPresentation(delta, directionality);
  if (semantic === 'positive') return 'success';
  if (semantic === 'negative') return 'error';
  return 'default';
}

/** One KPI tile: label, value or bars body, delta row (badge + optional arrow icon). */
const KPITile: React.FC<KPITileProps> = ({
  label,
  labelTooltip,
  labelTooltipSide = 'top',
  labelTooltipFormat,
  labelTooltipTrigger,
  value,
  delta,
  directionality,
  deltaLabel,
  deltaSubLabel,
  statStyle: statStyleProp,
  bars,
  barsMax,
  colorValue = false,
  trendData,
  sparklineLineStyle,
}) => {
  const presentation = getDeltaPresentation(delta, directionality);
  const badgeVariant = getDeltaBadgeVariant(presentation.semantic);
  const badgeClassName = getDeltaBadgeClassName(presentation.semantic);
  const statStyle = statStyleProp ?? 'big-number';
  const showBars = isBarsStatLayout(statStyle, bars);

  const sanitizedTrendData = React.useMemo(
    () =>
      trendData?.filter((p) => typeof p.y === 'number' && !Number.isNaN(p.y)),
    [trendData],
  );

  const valueText = React.useMemo(() => resolveDisplayValue(value), [value]);
  const deltaText = deltaLabel
    ? resolveDisplayValue(deltaLabel)
    : defaultFormatDeltaValue(delta);

  // `colorValue: 'auto-by-value'` resolves tone from the sign of the displayed
  // value. A described number carries its own sign; a display string has to be
  // parsed back, and unparseable text (`'—'`, `'N/A'`, …) falls through to
  // neutral tone.
  const rawValue = React.useMemo(
    () => displayValueNumber(value) ?? parseDisplayValue(valueText),
    [value, valueText],
  );

  const warnedDirtyDataRef = React.useRef(false);
  if (
    process.env.NODE_ENV !== 'production' &&
    trendData !== undefined &&
    sanitizedTrendData !== undefined &&
    sanitizedTrendData.length < trendData.length &&
    !warnedDirtyDataRef.current
  ) {
    warnedDirtyDataRef.current = true;
    console.warn(
      `[hen-ui/KPITile] trendData contained ${trendData.length - sanitizedTrendData.length} point(s) with non-numeric y values — they have been filtered out. Check the data source.`,
    );
  }

  const sparklineFallback = isSparklineFallback(statStyle, sanitizedTrendData);
  const warnedRef = React.useRef(false);
  if (
    process.env.NODE_ENV !== 'production' &&
    sparklineFallback &&
    !warnedRef.current
  ) {
    warnedRef.current = true;
    console.warn(
      '[hen-ui/KPITile] statStyle="sparkline" requires non-empty trendData with numeric y values; falling back to big-number.',
    );
  }

  const resolvedLineStyle =
    sparklineLineStyle === 'solid' || sparklineLineStyle === 'dashed'
      ? sparklineLineStyle
      : 'solid';
  const lineStyleWarnedRef = React.useRef(false);
  if (
    process.env.NODE_ENV !== 'production' &&
    sparklineLineStyle !== undefined &&
    sparklineLineStyle !== 'solid' &&
    sparklineLineStyle !== 'dashed' &&
    !lineStyleWarnedRef.current
  ) {
    lineStyleWarnedRef.current = true;
    console.warn(
      `[hen-ui/KPITile] Invalid sparklineLineStyle="${sparklineLineStyle}"; defaulting to "solid".`,
    );
  }
  const isSparklineLayout =
    statStyle === 'sparkline' &&
    !!sanitizedTrendData &&
    sanitizedTrendData.length > 0 &&
    !sparklineFallback;

  const showDelta =
    presentation.semantic !== 'neutral' ||
    deltaLabel !== undefined ||
    deltaSubLabel !== undefined;

  return (
    <Card
      data-kpi-tile
      variant='background-white'
      border='strokeLight'
      rounded='lg'
      className={cn('grow shrink-0 basis-auto', 'min-w-kpi-tile')}
    >
      <Card.Content padding='py-4 px-4' className='gap-xxs'>
        {labelTooltip?.trim() ? (
          <span className='flex min-w-0 w-full items-center gap-0.5'>
            <Text
              variant='b5'
              color='subtext'
              tag='span'
              className='min-w-0 truncate font-semibold uppercase tracking-wide'
              title={label}
            >
              {label}
            </Text>
            <Tooltip
              content={labelTooltip.trim()}
              side={labelTooltipSide}
              contentFormat={labelTooltipFormat}
              trigger={labelTooltipTrigger}
            >
              <Button
                type='button'
                variant='iconOnly'
                size='flush'
                iconBefore='info'
                className='shrink-0'
                aria-label={`Info about ${label}`}
              />
            </Tooltip>
          </span>
        ) : (
          <Text
            variant='b5'
            color='subtext'
            className='min-w-0 truncate font-semibold uppercase tracking-wide'
            title={label}
          >
            {label}
          </Text>
        )}
        {isSparklineLayout ? (
          <div className='min-w-0 w-full'>
            <StatStyles
              value={valueText}
              valueColor={
                colorValue
                  ? getValueColor(delta, directionality, colorValue, rawValue)
                  : undefined
              }
              className={kpiTileBigNumberStatClassName}
            />
            <SparklineChart
              data={sanitizedTrendData!}
              lineStyle={resolvedLineStyle}
              semantic={deltaToSemantic(delta, directionality)}
              className='mt-xs'
            />
          </div>
        ) : showBars ? (
          <div
            className={cn(
              'min-w-0 w-full min-h-0',
              statStyle === 'bars-vertical' &&
                'flex h-28 max-h-28 flex-col overflow-hidden',
              statStyle === 'bars-horizontal' && 'min-h-20 overflow-hidden',
            )}
            title={valueText || undefined}
          >
            <StatStyles
              bars={bars}
              max={barsMax}
              style={statStyle as 'bars-vertical' | 'bars-horizontal'}
              className={cn(
                'min-h-0 min-w-0 w-full',
                statStyle === 'bars-vertical' && 'h-full max-h-full flex-1',
                statStyle === 'bars-horizontal' && 'max-w-full',
              )}
            />
          </div>
        ) : (
          <div
            className='min-w-0 w-full truncate'
            title={valueText || undefined}
            data-kpi-value-text=''
          >
            <StatStyles
              value={valueText}
              valueColor={
                colorValue
                  ? getValueColor(delta, directionality, colorValue, rawValue)
                  : undefined
              }
              className={kpiTileBigNumberStatClassName}
            />
          </div>
        )}
        <Flex direction='row' align='center' gap='xxs' className='min-w-0'>
          {showDelta && presentation.arrowIcon ? (
            <Icon
              icon={presentation.arrowIcon}
              size='sm'
              aria-hidden
              className={cn('shrink-0', presentation.toneClassName)}
            />
          ) : null}
          {showDelta && (
            <Badge
              data-slot='badge'
              variant={badgeVariant}
              size='lg'
              className={cn('shrink-0 font-medium', badgeClassName)}
            >
              {deltaText}
              {deltaSubLabel ? ` ${deltaSubLabel}` : null}
            </Badge>
          )}
        </Flex>
      </Card.Content>
    </Card>
  );
};
